import React from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { BillingInfo } from "../../types/billing";
import { billingService } from "../../services/billingService";

interface ScheduledDowngradeNoticeProps {
  scheduledChanges: BillingInfo["scheduled_changes"];
  onCancel?: () => void;
}

export const ScheduledDowngradeNotice: React.FC<
  ScheduledDowngradeNoticeProps
> = ({ scheduledChanges, onCancel }) => {
  if (!scheduledChanges) return null;
  const formatDate = (dateString?: string) => {
    if (!dateString) return "end of billing period";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Determine what type of change this is
  const changeDetails = [];

  if (scheduledChanges.current_plan !== scheduledChanges.new_plan) {
    changeDetails.push({
      type: "plan",
      from: billingService.getPlanDisplayName(scheduledChanges.current_plan),
      to: billingService.getPlanDisplayName(scheduledChanges.new_plan),
    });
  }

  if (
    scheduledChanges.current_billing_cycle &&
    scheduledChanges.new_billing_cycle &&
    scheduledChanges.current_billing_cycle !==
      scheduledChanges.new_billing_cycle
  ) {
    changeDetails.push({
      type: "billing",
      from: scheduledChanges.current_billing_cycle,
      to: scheduledChanges.new_billing_cycle,
    });
  }

  if (
    scheduledChanges.current_editor_count !== undefined &&
    scheduledChanges.new_editor_count !== undefined &&
    scheduledChanges.current_editor_count !== scheduledChanges.new_editor_count
  ) {
    changeDetails.push({
      type: "editors",
      from: scheduledChanges.current_editor_count,
      to: scheduledChanges.new_editor_count,
    });
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <InformationCircleIcon className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            Scheduled Changes
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>{scheduledChanges.message}</p>
            {changeDetails.length > 0 && (
              <ul className="mt-2 space-y-1">
                {changeDetails.map((change, idx) => (
                  <li key={idx}>
                    {change.type === "plan" && (
                      <>
                        Plan: <strong>{change.from}</strong> →{" "}
                        <strong>{change.to}</strong>
                      </>
                    )}
                    {change.type === "billing" && (
                      <>
                        Billing: <strong>{change.from}</strong> →{" "}
                        <strong>{change.to}</strong>
                      </>
                    )}
                    {change.type === "editors" && (
                      <>
                        Editors: <strong>{change.from}</strong> →{" "}
                        <strong>{change.to}</strong>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2">
              Effective {formatDate(scheduledChanges.effective_date)}
            </p>
          </div>
          {onCancel && (
            <div className="mt-3">
              <button
                onClick={onCancel}
                className="text-sm font-medium text-yellow-800 hover:text-yellow-900 hover:underline"
              >
                Cancel downgrade
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
